import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, {
  MultiValue,
  MultiValueRemoveProps,
  StylesConfig,
  components,
} from "react-select";
import { Option } from "../../interface/Option";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";

interface InsurancePlanOption extends Option {
  dbId?: number;
  planName?: string;
  is_active?: boolean | null;
  status?: string;
  __inactive?: boolean;
}

interface ListOfInsuranceplanNameProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  qaIdPrefix?: string;
  [key: string]: any;
}

const boolValue = (value: any): boolean | null => {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  const lower = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (lower === "true") return true;
  if (lower === "false") return false;
  return null;
};

// Unknown status is different from inactive: never invent an inactive state.
const activeStatus = (item: any): boolean | null => {
  if (!item || typeof item !== "object") return null;
  if (
    [item.is_deleted, item.isDeleted, item.is_soft_deleted, item.isSoftDeleted,
      item.is_hard_deleted, item.isHardDeleted].some((v) => boolValue(v) === true)
  ) return false;

  const active = boolValue(item.is_active ?? item.isActive ?? item.active);
  const status = String(item.status ?? item.Status ?? "").trim().toLowerCase();
  if (active === false || ["inactive", "disabled"].includes(status)) return false;
  if (active === true || ["active", "enabled"].includes(status)) return true;
  return null;
};

const optionId = (item: any): string =>
  String(item?.value ?? item?.dbId ?? item?.id ?? item?.plan_id ?? item?.Plan_id ?? "").trim();

const isLocked = (item: InsurancePlanOption): boolean =>
  item.__inactive === true || activeStatus(item) === false;

const formatInsurancePlan = (item: any): InsurancePlanOption | null => {
  const rawId = item?.id ?? item?.plan_id ?? item?.Plan_id;
  const name = String(
    item?.Plan_name ?? item?.plan_name ?? item?.name ?? item?.planName ?? item?.title ?? ""
  ).trim();
  if (rawId === undefined || rawId === null || !name) return null;
  const id = String(rawId);
  const active = activeStatus(item);
  return {
    value: id,
    label: `(${id}) ${name}`,
    dbId: Number(rawId),
    planName: name,
    is_active: active,
    status: item?.status ?? item?.Status,
    __inactive: active === false,
  } as InsurancePlanOption;
};

const fetchAllInsurancePlans = async (): Promise<InsurancePlanOption[]> => {
  // No module-level cache: status may change between opening Edit screens.
  // Do not request active-only: we also need to identify previously linked inactive plans.
  const response = await InsuranceAxiosInstance.get("getInsurancePlan");
  const raw = response.data?.result ?? response.data?.getInsurancePlans ??
    response.data?.insurancePlans ?? response.data?.rows ??
    response.data?.data ?? response.data ?? [];
  const data: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.getInsurancePlans)
      ? raw.getInsurancePlans
      : Array.isArray(raw?.insurancePlans)
        ? raw.insurancePlans
        : Array.isArray(raw?.rows)
          ? raw.rows
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
  return data.map(formatInsurancePlan).filter(
    (item: InsurancePlanOption | null): item is InsurancePlanOption => item !== null
  );
};

const resolveSelections = (
  incoming: Option[],
  catalogue: InsurancePlanOption[],
  prior: InsurancePlanOption[]
): InsurancePlanOption[] => {
  const catalogueById = new Map(catalogue.map((item) => [optionId(item), item]));
  const priorById = new Map(prior.map((item) => [optionId(item), item]));
  const seen = new Set<string>();
  return incoming.map((original) => {
    const id = optionId(original);
    const current = catalogueById.get(id);
    const old = priorById.get(id);
    const knownStatus = current ? activeStatus(current) : null;
    const status = knownStatus ?? activeStatus(original) ??
      (old ? activeStatus(old) : null);
    return {
      ...(old || {}),
      ...original,
      ...(current || {}),
      value: id,
      // Keep the label already supplied by the relation, if present.
      label: original?.label || current?.label || old?.label || id,
      is_active: status,
      __inactive: status === false,
    } as InsurancePlanOption;
  }).filter((item) => {
    const id = optionId(item);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const ListOfInsuranceplanName = ({
  onChange,
  clear,
  defaultValue,
}: ListOfInsuranceplanNameProps) => {
  const [allOptions, setAllOptions] = useState<InsurancePlanOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<InsurancePlanOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [loaded, setLoaded] = useState(false);
  const requestSequence = useRef(0);

  const loadAllOptions = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const plans = await fetchAllInsurancePlans();
      if (sequence === requestSequence.current) {
        setAllOptions(plans);
        setLoaded(true);
      }
    } catch (error) {
      console.error("Error loading insurance plans:", error);
      if (sequence === requestSequence.current) {
        setAllOptions([]);
        setLoaded(false);
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAllOptions();
    return () => { requestSequence.current += 1; };
  }, [loadAllOptions]);

  // No implicit editing of parent state when a formerly linked plan turns inactive.
  useEffect(() => {
    if (clear || defaultValue === undefined) return;
    setSelectedOptions((previous) =>
      resolveSelections(Array.isArray(defaultValue) ? defaultValue : [], allOptions, previous)
    );
  }, [defaultValue, allOptions, clear]);

  // Clearing the form must not silently remove a linked inactive plan.
  useEffect(() => {
    if (!clear) return;
    setSelectedOptions((previous) => previous.filter(isLocked));
    setInputValue("");
  }, [clear]);

  const options = useMemo(() => {
    // Only explicitly confirmed active records may be offered for new selection.
    const activeOptions = allOptions.filter((option) => activeStatus(option) === true);
    const query = inputValue.trim().toLowerCase();
    if (!query) return activeOptions;
    return activeOptions.filter((option) =>
      String(option.planName ?? option.label.replace(/^\([^)]*\)\s*/, ""))
        .toLowerCase().includes(query)
    );
  }, [allOptions, inputValue]);

  const handleChange = (selected: MultiValue<InsurancePlanOption>) => {
    const proposed = [...selected];
    // Guard against Backspace, Remove, Clear and other react-select actions.
    const protectedItems = selectedOptions.filter((previous) =>
      isLocked(previous) && !proposed.some((item) => optionId(item) === optionId(previous))
    );
    const updated = resolveSelections(
      [...proposed, ...protectedItems], allOptions, selectedOptions
    );
    setSelectedOptions(updated);
    onChange(updated);
  };

  const customStyles: StylesConfig<InsurancePlanOption, true> = {
    multiValue: (base, props) => ({
      ...base,
      backgroundColor: isLocked(props.data) ? "#d1d5db" : "#007BFF",
      border: isLocked(props.data) ? "1px solid #94a3b8" : "none",
      borderRadius: "5px",
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      color: isLocked(props.data) ? "#334155" : "#FFFFFF",
      fontWeight: isLocked(props.data) ? 600 : 400,
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      display: isLocked(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" },
    }),
    option: (base, { isSelected, isFocused }) => ({
      ...base,
      backgroundColor: isSelected ? "#007BFF" : isFocused ? "#eaf2ff" : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      cursor: "pointer",
    }),
  };

  const MultiValueRemove = (props: MultiValueRemoveProps<InsurancePlanOption, true>) =>
    isLocked(props.data) ? null : <components.MultiValueRemove {...props} />;

  return (
    <Select<InsurancePlanOption, true>
      menuPosition="absolute"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      captureMenuScroll={true}
      isMulti
      isClearable={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      isLoading={loading}
      onInputChange={(value, actionMeta) => {
        if (actionMeta.action === "input-change") setInputValue(value);
        return value;
      }}
      placeholder="Type to search or select from list..."
      noOptionsMessage={() => loading ? "Loading..." : loaded ?
        "No active insurance plans found" : "Unable to load insurance plans"}
      styles={{
        ...customStyles,
        menu: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
        menuList: (base) => ({
          ...base,
          maxHeight: 280,
          overflowY: "auto",
          pointerEvents: "auto",
          WebkitOverflowScrolling: "touch",
        }),
      }}
      components={{ MultiValueRemove }}
    />
  );
};

export default ListOfInsuranceplanName;
